/**
 * React Hook for Eva Unit Core
 *
 * Provides a convenient way to access eva-unit-core conversion functions
 * within React components.
 */
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";

import {
  getEvaUnitCore,
  initializeEvaUnitCore,
  isEvaUnitCoreInitialized
} from "utils/evaUnitCoreInitializer";

export interface UseEvaUnitCoreResult {
  /**
   * Whether the eva-unit-core library is initialized and ready to use
   */
  isReady: boolean;

  /**
   * Convert a single value from one unit to another (WASM interface)
   * @param fromUnit - Source unit (e.g., "F", "psi")
   * @param toUnit - Target unit (e.g., "C", "kPa")
   * @param value - Single value to convert
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Converted value
   */
  convertUnitToUnit: (
    fromUnit: string,
    toUnit: string,
    value: number,
    precision?: number
  ) => number;

  /**
   * Convert a single value by unit name to a target unit type
   * @param fromUnit - Source unit (e.g., "F", "psi")
   * @param targetType - Target unit type: 0 = Hybrid, 1 = SI, 2 = Imperial
   * @param value - Single value to convert
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Converted value
   */
  convertByUnit: (
    fromUnit: string,
    targetType: number,
    value: number,
    precision?: number
  ) => number;

  /**
   * Convert array of values from one unit to another (modifies in place)
   * @param fromUnit - Source unit (e.g., "F", "psi")
   * @param toUnit - Target unit (e.g., "C", "kPa")
   * @param values - Float64Array of values to convert (modified in place)
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Result message string
   */
  convertUnitsToUnits: (
    fromUnit: string,
    toUnit: string,
    values: Float64Array,
    precision?: number
  ) => string;

  /**
   * Convert array of values by unit name (modifies in place)
   * @param fromUnit - Source unit (e.g., "F", "psi")
   * @param targetType - Target unit type: 0 = Hybrid, 1 = SI, 2 = Imperial
   * @param values - Float64Array of values to convert (modified in place)
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Result message string
   */
  convertByUnits: (
    fromUnit: string,
    targetType: number,
    values: Float64Array,
    precision?: number
  ) => string;

  /**
   * Get the target unit name based on the original unit and target type
   * @param originalUnit - Source unit (e.g., "F", "psi")
   * @param targetType - Target unit type: 0 = Hybrid, 1 = SI, 2 = Imperial
   * @returns Target unit name as string
   * @example
   * ```ts
   * const target = getTargetUnit("F", 1); // Returns "C" (SI)
   * const target = getTargetUnit("m", 0); // Returns "m" (Hybrid)
   * ```
   */
  getTargetUnit: (originalUnit: string, targetType: number) => string;

  /**
   * Check if a conversion is supported
   * @param fromUnit - Source unit
   * @param toUnit - Target unit
   * @returns true if conversion is supported, false otherwise
   */
  isConversionSupported: (fromUnit: string, toUnit: string) => boolean;

  /**
   * Get the library version
   * @returns Version string
   */
  version: () => string;
}

/**
 * Hook to access eva-unit-core conversion functions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isReady, convertUnitToUnit } = useEvaUnitCore();
 *
 *   if (!isReady) {
 *     return <div>Loading unit converter...</div>;
 *   }
 *
 *   const celsius = convertUnitToUnit("F", "C", 100);
 *   return <div>100°F = {celsius}°C</div>;
 * }
 * ```
 */
export function useEvaUnitCore(): UseEvaUnitCoreResult {
  const [isReady, setIsReady] = useState(isEvaUnitCoreInitialized());

  useEffect(() => {
    // If not initialized, trigger initialization
    if (!isEvaUnitCoreInitialized()) {
      initializeEvaUnitCore()
        .then(() => {
          setIsReady(true);
        })
        .catch((error) => {
          Sentry.captureException(error);
          Sentry.logger.error("[useEvaUnitCore] Initialization failed", { error });
        });
    }
  }, []);

  const unitCore = getEvaUnitCore();

  return {
    isReady,
    convertUnitToUnit: (
      fromUnit: string,
      toUnit: string,
      value: number,
      precision: number = -1
    ) => {
      if (!isReady || !unitCore) {
        Sentry.logger.warn(
          "[useEvaUnitCore] Library not ready, returning original value"
        );
        return value;
      }
      try {
        return unitCore.convert_unit_to_unit_wasm(fromUnit, toUnit, value, precision);
      } catch (error) {
        Sentry.logger.warn(
          Sentry.logger
            .fmt`[useEvaUnitCore] Conversion error from "${fromUnit}" to "${toUnit}": ${error}`
        );
        Sentry.captureException(error);
        return value;
      }
    },
    convertByUnit: (
      fromUnit: string,
      targetType: number,
      value: number,
      precision: number = -1
    ) => {
      if (!isReady || !unitCore) {
        Sentry.logger.warn(
          "[useEvaUnitCore] Library not ready, returning original value"
        );
        return value;
      }
      try {
        return unitCore.convert_by_unit(fromUnit, targetType, value, precision);
      } catch (error) {
        Sentry.logger.warn(
          Sentry.logger.fmt`[useEvaUnitCore] Conversion error for "${fromUnit}": ${error}`
        );
        Sentry.captureException(error);
        return value;
      }
    },
    convertUnitsToUnits: (
      fromUnit: string,
      toUnit: string,
      values: Float64Array,
      precision: number = -1
    ) => {
      if (!isReady || !unitCore) {
        Sentry.logger.warn("[useEvaUnitCore] Library not ready, returning error message");
        return "Error: Library not ready";
      }
      try {
        return unitCore.convert_units_to_units_wasm(fromUnit, toUnit, values, precision);
      } catch (error) {
        Sentry.logger.warn(
          Sentry.logger
            .fmt`[useEvaUnitCore] Conversion error from "${fromUnit}" to "${toUnit}": ${error}`
        );
        Sentry.captureException(error);
        return `Error: ${error}`;
      }
    },
    convertByUnits: (
      fromUnit: string,
      targetType: number,
      values: Float64Array,
      precision: number = -1
    ) => {
      if (!isReady || !unitCore) {
        Sentry.logger.warn("[useEvaUnitCore] Library not ready, returning error message");
        return "Error: Library not ready";
      }
      try {
        return unitCore.convert_by_units(fromUnit, targetType, values, precision);
      } catch (error) {
        Sentry.logger.warn(
          Sentry.logger.fmt`[useEvaUnitCore] Conversion error for "${fromUnit}": ${error}`
        );
        Sentry.captureException(error);
        return `Error: ${error}`;
      }
    },
    getTargetUnit: (originalUnit: string, targetType: number) => {
      if (!isReady || !unitCore) {
        Sentry.logger.warn("[useEvaUnitCore] Library not ready, returning original unit");
        return originalUnit;
      }
      try {
        return unitCore.get_target_unit_wasm(originalUnit, targetType);
      } catch (error) {
        Sentry.logger.warn(
          Sentry.logger
            .fmt`[useEvaUnitCore] Unit lookup error for "${originalUnit}": ${error}`
        );
        Sentry.captureException(error);
        return originalUnit;
      }
    },
    isConversionSupported: (fromUnit: string, toUnit: string) => {
      if (!isReady || !unitCore) {
        Sentry.logger.warn("[useEvaUnitCore] Library not ready, returning false");
        return false;
      }
      try {
        return unitCore.is_conversion_supported(fromUnit, toUnit);
      } catch (error) {
        Sentry.logger.warn(
          Sentry.logger
            .fmt`[useEvaUnitCore] Conversion support check error for "${fromUnit}" to "${toUnit}": ${error}`
        );
        Sentry.captureException(error);
        return false;
      }
    },
    version: () => {
      if (!unitCore) {
        return "not-initialized";
      }
      return unitCore.version();
    }
  };
}
